import { Component, OnInit, Output, EventEmitter, ViewChildren, QueryList, Input } from '@angular/core';
import { NgRedux } from '@redux/redux-compatibility.service';
import { AppState, actionsList } from 'src/app/shared/redux/store';
import { RevService } from 'src/app/shared/services/rev.service';
import {
  RevRule,
  RevDoc,
  RevStatus,
  RevDocStatusEnum,
} from 'src/app/shared/models/rev.model';
import { Unsubscribe } from 'redux';
import { EMPTY, merge, Observable, Subscription, timer, throwError, Subject, combineLatest } from 'rxjs';
import { AuthService } from 'src/app/shared/services/auth.service';
import { SharedService } from 'src/app/shared/services/shared.service';
import { TranslateService } from '@ngx-translate/core';
import { RevRuleCacheObject } from '@shared/models/rev-rule-cache-object.model';
import { expand, filter, map, switchMap, catchError, takeUntil, retryWhen, scan, delay } from 'rxjs/operators';
import { RevDocRuleTileComponent } from '../rev-doc-rule-tile/rev-doc-rule-tile.component';
import { SearchRunStatusEnum } from '@shared/models/common';
import { CatService } from '@shared/services/cat.service';
import { ParentTileChildrenSelectionResponse } from '@shared/models/parent-tile-children-selection-response.model';
import { ReferenceDocument } from '@shared/models/cat.model';
import { IntegrationService } from '@shared/services/integration.service';
import { DocumentPositionSyncService } from '@shared/services/document-position-sync.service';

@Component({
    selector: 'rev-doc-rules-list',
    templateUrl: './rev-doc-rules-list.component.html',
    styleUrls: ['./rev-doc-rules-list.component.scss'],
    standalone: false
})
export class RevDocRulesListComponent implements OnInit {
  loading: boolean;
  @Input() sortByPagePosition: boolean = false;
  @Input() sortImagesInPositionSync: boolean = false;
  @Output() onToggleRuleView = new EventEmitter<RevRule>();
  @Output() onToggleRuleViewSwitchDisabled = new EventEmitter<RevRule>();
  @Output() onHideRuleView = new EventEmitter<RevRule>();
  @Output() onShowRuleView = new EventEmitter<RevRule>();
  @Output() loadedMoreItems = new EventEmitter();
  referenceDocuments: ReferenceDocument[] = [];

  displayPageNum: boolean;
  un: Unsubscribe;
  getDataSub: Subscription;
  searchStatusTimer: any;
  searchStatusIntervalSec: number = 2;
  sub: Subscription;
  cancelPreviousSearch$ = new Subject<void>();
  isFilterActive: boolean = false;
  @ViewChildren('rulesTiles') rulesTile!: QueryList<RevDocRuleTileComponent>;
  @ViewChildren('imageTiles') imageTiles!: QueryList<RevDocRuleTileComponent>;
  private revRulesCache: Map<number, RevRuleCacheObject> = new Map();
  private visibleChildren: { [id: number]: boolean } = {};
  private onDestroy$ = new Subject();
  //private lastScrollTargetTile?: RevDocRuleTileComponent;
  
  get imageRulesInPositionList(): boolean {
    return this.sortByPagePosition && this.sortImagesInPositionSync;
  }

  get rulesList(): RevRule[] {
    const rules = this.ngRedux.getState().revDocRules.filter(
      rule => !rule.isImage || this.imageRulesInPositionList
    );
    if (!this.sortByPagePosition) {
      return rules;
    }

    return this.documentPositionSyncService.sortByPagePosition(rules);
  }

  get imagesList(): RevRule[] {
    if (this.imageRulesInPositionList) {
      return [];
    }
    return this.ngRedux.getState().revDocRules.filter((r) => r.isImage);
  }

  get curRevDoc(): RevDoc {
    return this.ngRedux.getState().curRevDoc;
  }
  get addToStore(): boolean {
    return this.ngRedux.getState().addToStore;
  }
  set addToStore(value: boolean) {
    this.ngRedux.getState().addToStore = value;
  }

  constructor(
    private ngRedux: NgRedux<AppState>,
    private revService: RevService,
    private authService: AuthService,
    private sharedService: SharedService,
    private translateService: TranslateService,
    private catService: CatService,
    private integrationService: IntegrationService,
    private documentPositionSyncService: DocumentPositionSyncService
  ) {
    this.un = this.ngRedux.subscribe(() => {
      const appState = this.ngRedux.getState();
      switch (appState._currentAction) {
        case actionsList.FILTER_REV_RULES:
          this.displayPageNum = true;
          this.isFilterActive = true;
          this.getData(null, false);
          this.addToStore = false; // Reset after use
          break;
        case actionsList.SELECT_REV_DOC_PAGE_NUM:
          this.displayPageNum = false;
          this.isFilterActive = false;

          this.getData(this.ngRedux.getState().curRevDocPageNum);
          break;
        case actionsList.SELECT_REV_RULE:
          this.onMouseLeave(this.ngRedux.getState().curRevRule);
          break;
        case actionsList.RELOAD_CAT_DOC_PAGE_REQ: {
          
          if (this.isFilterActive) {
            this.displayPageNum = true;
            // Reload from first page if filter is active
            this.ngRedux.getState().revRulesSearchParams.currLoadedRuleNum = 0;
            this.ngRedux.getState().currLoadedRuleNum = 0;
            //this.setFirstPageLoaded.emit();
            this.addToStore = false;
            this.getData(null, false);
            
          } else {
            this.displayPageNum = false;
            this.getData(this.ngRedux.getState().curRevDocPageNum, false);  
          }
          break;
        }
        case actionsList.INVALIDATE_PDF_CACHE: {
          
          this.revRulesCache = new Map();
          break;
        }
        
      }
    });
  }

  ngOnInit(): void {

    if (this.revService.shouldSearch) {
      
      this.getData(this.ngRedux.getState().curRevDocPageNum);
    }
    this.revService.shouldSearch = false;
    this.loading = true;

    this.catService.getCatPhraseTypes(this.ngRedux.getState().curRevDoc.catId).subscribe();

  }

  ngOnDestroy(): void {
    if (this.un) this.un();
    this.sub?.unsubscribe();

    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  getData(pageNum: number, useCache = true) {
    // Cancel any ongoing requests
    this.cancelPreviousSearch$.next(); // Emit a value to cancel the previous flow
    this.cancelPreviousSearch$ = new Subject<void>(); // Reinitialize the Subject for the new flow


    if (!this.addToStore) {
      
      this.ngRedux.getState().revDocRules = [];
      this.loading = true;

    // Dispatch loading action
      this.ngRedux.dispatch({
        type: actionsList.UPD_DOC_PAGE_LOADING,
        data: true,
      });
    }

    
    let nextPageNum;
    let  isNextPageCached = false
    let isCurrentPageCached = false;
    if (useCache) {
     
      // Check if the next page is in the cache  
      nextPageNum = pageNum + 1;
      isNextPageCached = this.revRulesCache.has(nextPageNum);
      
      // Check if current page is in cache
      isCurrentPageCached = this.revRulesCache.has(pageNum);
      if (isCurrentPageCached) {
        this.setPageFromCache(pageNum);
        this.catService.searchFinished$.next(true);
      }

      // If both pages are cached, exit early
      if (isCurrentPageCached && isNextPageCached) {

        return;
      }
    }

    // Prepare observables for current and next page
    const currentPageRequest$ = isCurrentPageCached
      ? EMPTY // Skip request if current page is cached
      : this.revService.getRevRulesLight(this.curRevDoc.docId, pageNum, this.ngRedux.getState().revRulesSearchParams)
          .pipe(
            map(data => {
              return {pageNum, data};
            }),
            catchError((err) => {
            // Handle the error locally and return an empty observable
              console.error(`Error fetching data for current page (${pageNum}):`, err);
              this.ngRedux.dispatch({
                type: actionsList.UPD_DOC_PAGE_LOADING,
                data: false,
              });
              this.sharedService.showInfoMessage({
                messageKey: 'translate.alert.searchError',
                infoType: 'error'
              });
              this.loading = false;

              this.loadedMoreItems.emit();
            
              return EMPTY; // Prevent the error from propagating to the merge stream
            })
          );

    const nextPageRequest$ = EMPTY;

    // Merge the requests so they emit independently
    this.getDataSub = merge(currentPageRequest$, nextPageRequest$)
      .pipe(takeUntil(this.cancelPreviousSearch$)) // Cancel both requests if a new request starts
      .subscribe(
        (response: { pageNum: number; data: RevRuleCacheObject }) => {

          // Handle current page response
          if (response.pageNum != null && response.pageNum === this.ngRedux.getState().curRevDocPageNum) {
              this.setRulesToStore(response.data);
              this.catService.searchFinished$.next(true);
          }
          if (response.pageNum === null) {
            
            this.addRulesToStore(response.data);
            this.catService.searchFinished$.next(true);
            this.loadedMoreItems.emit();
          }
          // Save data to cache
          if (pageNum !== null) {
            this.revRulesCache.set(response.pageNum, response.data);
          }

          this.integrationService.getAvailableReferenceDocs(this.curRevDoc.catVersionId)
            .subscribe((docs) => {
              this.referenceDocuments = docs;
            });

        },
        (err) => {
          console.error('Unhandled error:', err);
        }
      );
  }

  // Add rules to store without replacing existing ones
  addRulesToStore(cachedData: RevRuleCacheObject) {
    this.ngRedux.dispatch({
      type: actionsList.ADD_REV_DOC_RULES,
      data: cachedData.revRules,
    });
    this.loading = false;
    // Dispatch loading complete
    this.ngRedux.dispatch({
      type: actionsList.UPD_DOC_PAGE_LOADING,
      data: false,
    });
  }

  setRulesToStore(cachedData: RevRuleCacheObject) {
    this.ngRedux.dispatch({
      type: actionsList.GET_REV_DOC_RULES,
      data: cachedData.revRules,
      reply: cachedData.reply,
    });
    this.loading = false;
    // Dispatch loading complete
    this.ngRedux.dispatch({
      type: actionsList.UPD_DOC_PAGE_LOADING,
      data: false,
    });
  }


  setPageFromCache(pageNum: number) {
    const cachedData = this.revRulesCache.get(pageNum);
    this.setRulesToStore(cachedData);
  }

  scrollToRule(rule: RevRule): boolean {
    const tile = this.rulesTile.find(item => item.rule === rule);
    if (!tile) {
      return false;
    }
/*     if (this.lastScrollTargetTile !== tile) {
      this.lastScrollTargetTile?.setScrollTarget(false);
      tile.setScrollTarget(true);
      this.lastScrollTargetTile = tile;
    } */
    tile.scrollIntoView();
    return true;
  }

  scrollToFirstRuleOnPage(pageNum: number): boolean {
    const firstRuleOnPage = this.rulesList.find(rule => rule.pageNum === pageNum);
    return firstRuleOnPage ? this.scrollToRule(firstRuleOnPage) : false;
  }

  pullSearchStatus$(searchId: number): Observable<SearchRunStatusEnum> {
    return this.authService.getSearchStatus(searchId).pipe(
      expand((status) => {
        if (status === SearchRunStatusEnum.FINISHED || status === SearchRunStatusEnum.ERROR) {
          return EMPTY; // Stop polling
        }
        return timer(this.searchStatusIntervalSec * 1000).pipe(
          switchMap(() =>
            this.authService.getSearchStatus(searchId)
          )
        );
      }),
      retryWhen((errors) =>
        errors.pipe(
          scan((retryCount, err) => {
            if (retryCount >= 2) {
              console.error('Error occurred 3 times during polling:', err);
              throw err; // Propagate the error to the parent observable
            }
            console.warn(`Retrying polling (${retryCount + 1}/3)...`);
            return retryCount + 1;
          }, 0),
          delay(this.searchStatusIntervalSec * 1000) // Delay between retries
        )
      ),
      takeUntil(this.cancelPreviousSearch$) // Stop polling if a new request starts
    );
  }

  pullSearchStatus(searchId: number, onFinished, onError) {
    this.sub?.unsubscribe();
    this.sub = this.authService.getSearchStatus(searchId).subscribe(
      (status) => {
        if (status == SearchRunStatusEnum.FINISHED) {
          onFinished();
        } else if (status == SearchRunStatusEnum.ERROR) {
          onError();
        } else {
          setTimeout(() => {
            this.pullSearchStatus(searchId, onFinished, onError);
          }, this.searchStatusIntervalSec * 1000);
        }
      },
      (err) => {
        onError();
      }
    );
  }

  onToggleRuleViewClicked(rule: RevRule) {
    this.onToggleRuleView.emit(rule);
  }

  onToggleRuleViewClickedSwitchDisabled(rule: RevRule) {
    this.onToggleRuleViewSwitchDisabled.emit(rule);
  }

  onGoToRulePage(rule: RevRule) {
    this.onToggleRuleView.emit(rule);
  }

  onMouseEnter(rule: RevRule) {
    this.onShowRuleView.emit(rule);
  }

  onMouseLeave(rule: RevRule) {
    if (!rule.viewSw) this.onHideRuleView.emit(rule);
  }

  get isSearchActive() {
    let searchParams = this.ngRedux.getState().revRulesSearchParams;
    return searchParams && 
      (searchParams.phrase 
        || searchParams.ruleStatus 
        || searchParams.contentType 
        || searchParams.matchType 
        || searchParams.ruleType 
        || (searchParams.subTypeList && searchParams.subTypeList.length > 0));
  }

  reloadData() {
    this.getData(this.ngRedux.getState().curRevDocPageNum, false);
  }

  hasPrevRule(index: number) {
    
    return index != 0;
  }
  hasPrevImage(index: number) {
  
    return index != 0 || this.rulesList.length > 0;
  }

  hasNextRule(index: number) {
    if (this.imageRulesInPositionList) {
      return index < this.rulesList.length - 1;
    }
    return index < this.rulesList.length - 1 || this.imagesList.length > 0;
  }
  hasNextImage(index: number) {

    return index < (this.imagesList.length-1);
  }
  showPrevRule(index: number) {
    if (index != 0) {
    
      //this.rulesList[index - 1].showRuleDetails$.next();
      this.rulesTile.toArray()[index-1].openRuleDetails();
    }
  }
  showPrevImage(index: number) {
    if (index != 0) {
      //this.imagesList[index - 1].showRuleDetails$.next();
      this.imageTiles.toArray()[index-1].openRuleDetails();
    } else if (this.rulesList.length > 0) { 
      
      //this.rulesList[this.rulesList.length - 1].showRuleDetails$.next();
      let rulesTileArray = this.rulesTile.toArray();
      rulesTileArray[rulesTileArray.length-1].openRuleDetails();
    }
  }
  showNextRule(index: number) {
    if (index < this.rulesList.length - 1) {
      this.rulesTile.toArray()[index + 1].openRuleDetails();
    } else if (!this.imageRulesInPositionList && this.imagesList.length > 0) {
      this.imageTiles.toArray()[0].openRuleDetails();
    }

    
  }
  showNextImage(index: number) {
    if (index < this.imagesList.length - 1) {
      //this.imagesList[index + 1].showRuleDetails$.next();
      this.imageTiles.toArray()[index+1].openRuleDetails();
    }
  }
toggleChildrenGroup(ruleId: number) {
  this.visibleChildren[ruleId] = !this.visibleChildren[ruleId];
}
handleToggle(collapsed: boolean, type: string, rule: RevRule) {

}

}
